/**
 * 从 Spine JSON（或 skelDataToJsonCompat 生成的兼容对象）解析生成初始 AvatarConfig
 * Selection 由运营人员手动创建，不再自动从 JSON 解析
 */
export function parseSpineToConfig(spineJson: any): any {
  // 从 Spine JSON 提取动画
  // 支持两种格式：
  //   1. 原始 JSON: animations 是 { animName: {...} } 的对象
  //   2. skelDataToJsonCompat: animations 是 { animName: {} } 的对象（从 SkeletonData 提取）
  const rawAnimations = spineJson.animations || {};
  const animations: any[] = Object.keys(rawAnimations).map((animName) => ({
    id: animName,
    name: animName,
    pro: 0,
    loop: true,
  }));

  return {
    selection: [], // Selection 由运营手动创建
    tonings: [],
    animations: animations,
    requireCategories: [], // 随机必选的 category
  };
}

/**
 * 从 Spine SkeletonData 提取出与 JSON 格式兼容的对象
 * 用于 .skel 二进制文件加载后，让下游代码无需关心数据来源
 *
 * @param skeletonData - 由 SkeletonBinary.readSkeletonData 返回的对象
 * @returns 与 Spine JSON 格式兼容的对象
 */
export function skelDataToJsonCompat(skeletonData: any): any {
  // skeleton 元信息
  const skeleton: any = {
    hash: skeletonData.hash || "",
    spine: skeletonData.version || "",
    width: skeletonData.width || 0,
    height: skeletonData.height || 0,
  };

  // slots
  const slots = (skeletonData.slots || []).map((slot: any) => ({
    name: slot.name,
    bone: slot.boneData?.name || "",
    attachment: slot.attachmentName || undefined,
  }));

  // skins（保留 attachments 结构以兼容 parseSkins / validateSkinNames / ConfigValidation）
  const skins = (skeletonData.skins || []).map((skin: any) => {
    const attachments: Record<string, Record<string, any>> = {};
    
    // Spine 4.x: getAttachments() return Array<SkinEntry>
    if (typeof skin.getAttachments === "function") {
      const entries = skin.getAttachments();
      if (entries && Array.isArray(entries)) {
        for (const entry of entries) {
          if (!entry) continue;
          const slotIndex = entry.slotIndex;
          const attName = entry.name;
          const attachment = entry.attachment;
          if (slotIndex === undefined || attName === undefined) continue;
          
          const slotData = skeletonData.slots?.[slotIndex];
          const slotName = slotData?.name || `slot_${slotIndex}`;
          if (!attachments[slotName]) attachments[slotName] = {};
          attachments[slotName][attName] = attachment ? { name: attName } : {};
        }
      }
    } else if (skin.attachments) {
      // Spine 3.8: skin.attachments is a Sparse Array (indexed by slotIndex) of objects (indexed by attachment name)
      for (let slotIndex = 0; slotIndex < skin.attachments.length; slotIndex++) {
        const slotDict = skin.attachments[slotIndex];
        if (!slotDict) continue; // Sparse array hole
        
        for (const attName of Object.keys(slotDict)) {
          const attachment = slotDict[attName];
          const slotData = skeletonData.slots?.[slotIndex];
          const slotName = slotData?.name || `slot_${slotIndex}`;
          
          if (!attachments[slotName]) attachments[slotName] = {};
          attachments[slotName][attName] = attachment ? { name: attName } : {};
        }
      }
    }

    return {
      name: skin.name,
      attachments: Object.keys(attachments).length > 0 ? attachments : undefined,
    };
  });

  // animations（只需要 key 列表，值为空对象即可）
  const animations: Record<string, any> = {};
  for (const anim of skeletonData.animations || []) {
    animations[anim.name] = {};
  }

  return {
    skeleton,
    slots,
    skins,
    animations,
    // 标记为从 .skel 转换而来
    _fromSkel: true,
  };
}

/**
 * 验证 Spine JSON 格式
 */
export function validateSpineJson(data: unknown): data is any {
  if (typeof data !== "object" || data === null) {
    return false;
  }

  const json = data as Record<string, unknown>;

  // 必须有 skeleton 对象
  if (typeof json.skeleton !== "object" || json.skeleton === null) {
    return false;
  }

  // 必须有 skins 数组
  if (!Array.isArray(json.skins)) {
    return false;
  }

  // 必须有 animations 对象
  if (typeof json.animations !== "object" || json.animations === null) {
    return false;
  }

  return true;
}

/**
 * Skin name 校验错误
 */
export interface SkinNameError {
  skinName: string;
  errors: string[];
}

/**
 * 校验 Skin name 规则：
 * 1. 不能有空格
 * 2. 不能有特殊符号（只允许字母、数字、下划线、斜杠）
 * 3. 最少要有 2 层（至少 1 个斜杠）
 * 4. 最多有 3 层（最多 2 个斜杠）
 */
export function validateSkinNames(spineJson: any): SkinNameError[] {
  const errors: SkinNameError[] = [];
  const skins = spineJson?.skins || [];

  // 允许的字符：字母、数字、下划线、斜杠
  const validPattern = /^[a-zA-Z0-9_/]+$/;

  for (const skin of skins) {
    const name = skin.name;
    if (name === "default") continue; // 跳过 default skin

    const skinErrors: string[] = [];

    // 1. 检查空格
    if (name.includes(" ")) {
      skinErrors.push("包含空格");
    }

    // 2. 检查特殊符号
    if (!validPattern.test(name)) {
      skinErrors.push("包含特殊符号（只允许字母、数字、下划线、斜杠）");
    }

    // 3. 检查层数
    const parts = name.split("/");
    if (parts.length < 2) {
      skinErrors.push(`层级不足：当前 ${parts.length} 层，最少需要 2 层`);
    }
    if (parts.length > 3) {
      skinErrors.push(`层级过多：当前 ${parts.length} 层，最多允许 3 层`);
    }

    // 4. 检查每层是否为空
    if (parts.some((p) => p === "")) {
      skinErrors.push("存在空层级（连续的斜杠或首尾斜杠）");
    }

    if (skinErrors.length > 0) {
      errors.push({ skinName: name, errors: skinErrors });
    }
  }

  return errors;
}

/**
 * 提取 category：2层取第一级，3层取前两级
 * face/eye → face
 * face/pupil/pupil_03 → face/pupil
 */
/**
 * 提取 category：2层取第一级，3层取前两级
 * face/eye → face
 * face/pupil/pupil_03 → face/pupil
 * 注意：skin name 必须是 2-3 层，由 validateSkinNames 校验保证
 */
export function getSkinCategory(skinName: string): string {
  const parts = skinName.split("/");
  if (parts.length === 3) {
    return `${parts[0]}/${parts[1]}`;
  }
  if (parts.length === 2) {
    return parts[0];
  }
  // 不应该到这里，校验应该已经拦截
  console.error("[getSkinCategory] invalid skin name:", skinName);
  return skinName;
}

/**
 * 从 skinName 生成确定性唯一 SKU ID（基于 DJB2 hash）
 */
export function generateSkuId(skinName: string): string {
  let hash = 5381;
  for (let i = 0; i < skinName.length; i++) {
    hash = (hash * 33) ^ skinName.charCodeAt(i);
  }
  return Math.abs(hash).toString(36).padStart(8, "0").slice(0, 8);
}

/**
 * 解析后的 Skin 数据结构
 */
export interface ParsedSkin {
  name: string;
  skuId: string;
  category: string;
  slots: string[];
  attachmentCount: number;
}

/**
 * 解析 Spine JSON 中的 skins，提取 category、skuId 等信息
 */
export function parseSkins(spineJson: any): ParsedSkin[] {
  const skins = spineJson?.skins || [];
  const result: ParsedSkin[] = [];

  for (const skin of skins) {
    if (skin.name === "default") continue;

    const slots = skin.attachments ? Object.keys(skin.attachments) : [];
    let attachmentCount = 0;
    if (skin.attachments) {
      for (const slotName of slots) {
        attachmentCount += Object.keys(skin.attachments[slotName]).length;
      }
    }

    result.push({
      name: skin.name,
      skuId: generateSkuId(skin.name),
      category: getSkinCategory(skin.name),
      slots,
      attachmentCount,
    });
  }

  return result;
}

/**
 * 按 category 分组 skins
 */
export function groupSkinsByCategory(
  skins: ParsedSkin[]
): Map<string, ParsedSkin[]> {
  const map = new Map<string, ParsedSkin[]>();
  for (const skin of skins) {
    if (!map.has(skin.category)) {
      map.set(skin.category, []);
    }
    map.get(skin.category)!.push(skin);
  }
  return map;
}

// Atlas 格式关键字
const PAGE_KEYWORDS = [
  "size:",
  "format:",
  "filter:",
  "repeat:",
  "pma:",
  "scale:",
];
const REGION_PROPERTY_KEYWORDS = [
  "index:",
  "bounds:",
  "offsets:",
  "rotate:",
  "split:",
  "pad:",
];

function isPagePropertyLine(line: string): boolean {
  const stripped = line.trim();
  return PAGE_KEYWORDS.some((kw) => stripped.startsWith(kw));
}

function isRegionPropertyLine(line: string): boolean {
  const stripped = line.trim();
  return REGION_PROPERTY_KEYWORDS.some((kw) => stripped.startsWith(kw));
}

/**
 * 从 atlas 内容中提取所有 region 名称
 */
export function extractAtlasRegionNames(atlasContent: string): string[] {
  const lines = atlasContent.split("\n");
  const regions: string[] = [];
  let inTextureSection = false;

  for (let i = 0; i < lines.length; i++) {
    const stripped = lines[i].trim();

    if (!stripped) continue;

    if (i === 0 || stripped.endsWith(".png")) {
      inTextureSection = true;
      continue;
    }

    if (isPagePropertyLine(stripped) || isRegionPropertyLine(stripped)) continue;

    if (inTextureSection) {
      regions.push(stripped);
    }
  }

  return regions;
}

/**
 * 从 Spine JSON 中提取所有 attachment name（带路径前缀的）
 */
function extractAttachmentNames(spineJson: any): Set<string> {
  const names = new Set<string>();
  const skins = spineJson?.skins || [];

  for (const skin of skins) {
    const attachments = skin.attachments || {};
    for (const slotName of Object.keys(attachments)) {
      for (const [key, value] of Object.entries(attachments[slotName])) {
        const att = value as any;
        const name = att.name || att.path || key;
        names.add(name);
      }
    }
  }

  return names;
}

export interface AtlasMismatchInfo {
  atlasFileName: string;
  prefix: string;
  missingRegions: { original: string; fixed: string }[];
}

/**
 * 检测多纹理 atlas 中 region name 是否缺少路径前缀。
 * 当 JSON 中的 attachment name 为 "background/background_02" 而 atlas 中只有 "background_02" 时触发。
 */
export function detectAtlasRegionMismatch(
  spineJson: any,
  atlasTexts: Record<string, string>,
): AtlasMismatchInfo[] {
  if (Object.keys(atlasTexts).length <= 1) return [];

  const attachmentNames = extractAttachmentNames(spineJson);
  const result: AtlasMismatchInfo[] = [];

  for (const [atlasFileName, atlasContent] of Object.entries(atlasTexts)) {
    const stem = atlasFileName.replace(/\.atlas$/i, "");
    const prefix = stem.replace(/^[a-zA-Z0-9]+-/, "");
    const regions = extractAtlasRegionNames(atlasContent);

    const missingRegions: { original: string; fixed: string }[] = [];

    for (const region of regions) {
      if (region.includes("/")) continue;

      const withPrefix = `${prefix}/${region}`;
      if (attachmentNames.has(withPrefix) && !attachmentNames.has(region)) {
        missingRegions.push({ original: region, fixed: withPrefix });
      }
    }

    if (missingRegions.length > 0) {
      result.push({ atlasFileName, prefix, missingRegions });
    }
  }

  return result;
}

/**
 * 修复 atlas 内容：为 region name 添加文件名前缀。
 * 参考 modify_atlas_prefix.py 的逻辑。
 */
export function addAtlasPrefixes(
  atlasContent: string,
  prefix: string,
): string {
  const lines = atlasContent.split("\n");
  const result: string[] = [];
  let inTextureSection = false;

  for (let i = 0; i < lines.length; i++) {
    const stripped = lines[i].trim();

    if (!stripped) {
      result.push(lines[i]);
      continue;
    }

    if (i === 0 || stripped.endsWith(".png")) {
      inTextureSection = true;
      result.push(lines[i]);
      continue;
    }

    if (isPagePropertyLine(stripped) || isRegionPropertyLine(stripped)) {
      result.push(lines[i]);
      continue;
    }

    if (inTextureSection && !stripped.includes("/")) {
      const indent = lines[i].substring(
        0,
        lines[i].length - lines[i].trimStart().length,
      );
      result.push(`${indent}${prefix}/${stripped}`);
    } else {
      result.push(lines[i]);
    }
  }

  return result.join("\n");
}

/**
 * 批量修复多个 atlas 文件内容
 */
export function fixAtlasTexts(
  atlasTexts: Record<string, string>,
  mismatches: AtlasMismatchInfo[],
): Record<string, string> {
  const fixed = { ...atlasTexts };
  for (const mismatch of mismatches) {
    if (fixed[mismatch.atlasFileName]) {
      fixed[mismatch.atlasFileName] = addAtlasPrefixes(
        fixed[mismatch.atlasFileName],
        mismatch.prefix,
      );
    }
  }
  return fixed;
}

/**
 * 读取文件为文本
 */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("读取文件失败"));
    reader.readAsText(file);
  });
}

/**
 * 读取文件为 Data URL
 */
export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("读取文件失败"));
    reader.readAsDataURL(file);
  });
}

/**
 * 读取文件为 ArrayBuffer（用于 .skel 二进制文件）
 */
export function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error("读取文件失败"));
    reader.readAsArrayBuffer(file);
  });
}
